import React from 'react';
import { format, isSameMonth } from 'date-fns';
import { useTranslation } from 'react-i18next';

import styles from '../styles/LandingPageLifts.module.scss';

import { OmaLummeApp } from '../../../omaLummeContext';

import Liftup from '../../Common/Liftup';

// Actions.
import { fetchPriceFixes } from '../../../actions/priceFixing';

// Types.
import { PriceFixUI } from '../../../types/contract';

// Utils.
import { roundNumber, setPriceFormat } from '../../../utils/billing';
import { isActiveUserB2B } from '../../../utils/customer';

const PriceFixLift: React.FunctionComponent = () => {
  const { t } = useTranslation();
  const { config, users, selectedMeteringPoint, selectedMeteringPointContract } = OmaLummeApp.useState();

  const [allFixings, setAllFixings] = React.useState<PriceFixUI[] | undefined>(undefined);

  const formatFixTimeSpan = (fixing: PriceFixUI): string => {
    const start = new Date(fixing.priceFixOfferData.Price_start_date);
    const end = new Date(fixing.priceFixOfferData.Price_end_date);

    if (isSameMonth(start, end)) {
      return `${format(end, 'MM/yyyy')}`;
    }

    return `${format(start, 'MM')}–${format(end, 'MM/yyyy')}`;
  };

  React.useEffect(() => {
    let unMounted = false;

    async function getFixings(): Promise<void> {
      if (selectedMeteringPointContract && !unMounted) {
        const existingFixings = await fetchPriceFixes(
          selectedMeteringPointContract.customerId,
          users.activeUser.userId,
          selectedMeteringPointContract.contractCode,
        );

        if (!existingFixings) {
          return;
        } else {
          const sortedFixings = existingFixings.sort(
            (a, b) => new Date(a.priceFixOfferData.Price_start_date).getTime() - new Date(b.priceFixOfferData.Price_start_date).getTime(),
          );
          if (sortedFixings && !unMounted) {
            setAllFixings(sortedFixings);
          }
        }
      }
    }
    if (!unMounted) {
      getFixings();
    }

    return (): void => {
      unMounted = true;
    };
  }, [selectedMeteringPointContract, users.activeUser]);

  const meteringPointFixings = allFixings?.filter((f) => f.meteringPointId === selectedMeteringPoint?.meteringPointId);

  const currentFixings = meteringPointFixings?.filter(
    (f) => new Date() >= new Date(f.priceFixOfferData.Price_start_date) && new Date() <= new Date(f.priceFixOfferData.Price_end_date),
  );
  const futureFixings = meteringPointFixings?.filter((f) => f.priceFixOfferData && new Date(f.priceFixOfferData.Price_start_date) > new Date());

  let current = false;
  let future = false;
  let fixings = undefined;

  if (currentFixings && currentFixings.length > 0) {
    current = true;
    fixings = currentFixings;
  } else if (futureFixings && futureFixings.length > 0) {
    future = true;
    fixings = futureFixings.filter(
      (f) =>
        f.priceFixOfferData.Price_start_date === futureFixings[0].priceFixOfferData.Price_start_date &&
        f.priceFixOfferData.Price_end_date === futureFixings[0].priceFixOfferData.Price_end_date,
    );
  }

  const percentageText = (fixings: PriceFixUI[], index = 0) => {
    if (
      fixings.length > 1 &&
      fixings[0].percentage === 50 &&
      fixings[1].percentage === 50 &&
      fixings[0].priceFixOfferData.Price_start_date === fixings[1].priceFixOfferData.Price_start_date &&
      fixings[0].priceFixOfferData.Price_end_date === fixings[1].priceFixOfferData.Price_end_date
    ) {
      return <span className={styles.percentage}>50+50 % {t(`manageContracts:spotPriceFixing.percentageFixing`)}</span>;
    } else if (fixings[index].percentage === 50) {
      return <span className={styles.percentage}>50 % {t(`manageContracts:spotPriceFixing.percentageFixing`)}</span>;
    }
    return;
  };

  const priceField = isActiveUserB2B(users) ? 'priceVat0' : 'priceWithVat';

  return (
    <>
      {config?.priceFixing === true && ((currentFixings && currentFixings.length > 0) || (futureFixings && futureFixings.length > 0)) && (
        <Liftup link={`/spotPriceFixing${!currentFixings || currentFixings.length === 0 ? '/upcoming' : ''}`} className="turquoise">
          <h4>{t('manageContracts:spotPriceFixing.priceFixLiftTitle')}</h4>
          {fixings && (
            <>
              <span className={styles.sans}>
                {t(
                  `manageContracts:spotPriceFixing.${fixings.length > 1 ? 'priceFixLifts' : 'priceFixLift'}${current ? 'Now' : future ? 'Next' : ''}`,
                )}{' '}
                ({formatFixTimeSpan(fixings[0])})
                {fixings.length > 1 && current && (
                  <>
                    {' '}
                    {t(`manageContracts:spotPriceFixing.priceFixLiftsAnd`)} ({formatFixTimeSpan(fixings[1])})
                  </>
                )}
              </span>
              <h3 className="h2">
                {setPriceFormat(roundNumber(fixings[0].priceFixOfferData[priceField]))}&nbsp;
                <span>{t('consumption:nordPoolGraph.spotUnit')}</span>
                {percentageText(fixings)}
              </h3>
              {fixings.length > 1 && (
                <h3 className="h2">
                  {setPriceFormat(roundNumber(fixings[1].priceFixOfferData[priceField]))}&nbsp;
                  <span>{t('consumption:nordPoolGraph.spotUnit')}</span>
                  {percentageText(fixings, 1)}
                </h3>
              )}
            </>
          )}
          {(!fixings || fixings.length === 0) && <p>{t(`manageContracts:spotPriceFixing.priceFixLiftNoValidOrUpcoming`)}</p>}
        </Liftup>
      )}
    </>
  );
};

export default PriceFixLift;
